import { CalculatedLine, Component } from '@paragon/contract-library';
import { findAndCalculateLine } from '@paragon/relative-geometry';

import { SelectableItemEntry } from '../library/selection/types';
import type { RootState } from '../store';
import { getActiveComponentDesign } from '../store/selectors/getActiveComponentDesignSelector';
import { getProjectLayoutModel } from '../store/selectors/getProjectLayoutModel';
import { getSelectedItemEntries } from '../store/selectors/getSelectedItemSelector';
import { getActiveLevelOrNull } from '../store/selectors/layoutDataSelectors';
import { isLayoutViewActive } from '../store/selectors/layoutModeSelectors';
import { describeBearingGeometry, describeLumber, describeMemberGeometry, roundPoint } from './tools/helpers';
import {
  describeFloorArea,
  describeRoofPlane,
  describeTrussEnvelope,
  describeWall,
  describeWorkLine,
} from './tools/layoutDescriptions';

/**
 * A truss's reference line: a point it passes through and its direction, in the truss's coordinates. It runs on in both
 * directions.
 */
function describeReferenceLine(line: CalculatedLine, component: Component) {
  try {
    const calculated = findAndCalculateLine(line.guid, component);
    if (calculated == null) return {};
    const direction = calculated.direction;
    const round = (value: number) => Math.round(value * 1000) / 1000;
    return {
      through: roundPoint(calculated.basePoint),
      direction: { x: round(direction.x), y: round(direction.y) },
    };
  } catch {
    return {};
  }
}

function renameType<T extends { type: unknown }>(type: string, { type: kind, ...rest }: T) {
  return { type, kind, ...rest };
}

/**
 * Describes a selected or clicked item by its type, the name the app shows for it, and where it is, so "this web" or
 * "this wall" needs no further reading: on the open truss in the truss's coordinates, given its `component`, and in the
 * layout in plan coordinates, given the `state`.
 */
export function describeSelectedItem(
  entry: SelectableItemEntry,
  component: Component | null = null,
  state: RootState | null = null,
) {
  if (state != null)
    switch (entry.type) {
      case 'wall':
      case 'beam':
        // The wall's own type, such as Wall or Beam, becomes its kind.
        return renameType(entry.type, describeWall(entry.item));
      case 'trussEnvelope':
        return renameType(entry.type, describeTrussEnvelope(state, entry.item));
      case 'roofPlane':
        return { type: entry.type, ...describeRoofPlane(state, entry.item) };
      case 'featurePatternContainer':
        return { type: 'floorArea', ...describeFloorArea(state, entry.item) };
      case 'workLine':
        return { type: entry.type, ...describeWorkLine(entry.item) };
    }

  if (component != null)
    switch (entry.type) {
      case 'member':
      case 'brace':
        return {
          type: entry.type,
          name: entry.item.name,
          lumber: describeLumber(entry.item),
          ...describeMemberGeometry(entry.item, component),
        };
      case 'bearing':
        return { type: entry.type, name: entry.item.name, ...describeBearingGeometry(entry.item, component) };
      case 'analogJoint':
        // Named as the analysis and get_truss name joints.
        return { type: 'joint', name: `Joint ${entry.item.name}`, at: roundPoint(entry.item) };
      case 'gusset':
        return { type: entry.type, outline: entry.item.geometry.map(roundPoint) };
      case 'referenceLine':
        return {
          type: entry.type,
          ...('name' in entry.item ? { name: entry.item.name } : {}),
          kind: entry.item.lineType,
          ...describeReferenceLine(entry.item, component),
        };
    }

  const item = entry.item as { name?: unknown; component?: { name?: unknown } } | null;
  const name = typeof item?.name === 'string' ? item.name : item?.component?.name;
  return { type: entry.type, ...(typeof name === 'string' ? { name } : {}) };
}

/**
 * What the designer is looking at, sent with every request: the view, the level, the open Truss, and the selection.
 */
export function getAssistantSnapshot(state: RootState) {
  const project = getProjectLayoutModel(state);
  const activeLevel = getActiveLevelOrNull(state);
  const activeComponentDesign = getActiveComponentDesign(state);

  return {
    project: project?.name ?? null,
    view: isLayoutViewActive(state) ? 'layout' : 'truss editor',
    level: activeLevel?.name ?? null,
    openTruss: activeComponentDesign?.component.name ?? null,
    selection: getSelectedItemEntries(state).map((entry) =>
      describeSelectedItem(entry, activeComponentDesign?.component ?? null, state),
    ),
  };
}
